import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { motion } from "framer-motion";
import { formatMoney as formatCurrency } from "../lib/currency";
import { supabase } from "../lib/supabase";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../contexts/AuthContext";
import logo from "../assets/spacem8-logo.png";
import {
  ArrowUpRight,
  CalendarDays,
  Download,
  Printer,
  BrainCircuit,
  Clock3,
  Wallet,
  Building2,
  ReceiptText,
  Search,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
} from "recharts";

function Button({ children, className = "", variant = "default", ...props }) {
  const base =
    "inline-flex items-center justify-center whitespace-nowrap transition-all font-medium disabled:pointer-events-none disabled:opacity-50";

  const styles =
    variant === "outline"
      ? "border"
      : "bg-orange-500 text-white hover:bg-orange-400 shadow-sm";

  return (
    <button className={`${base} ${styles} ${className}`} {...props}>
      {children}
    </button>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`flex w-full rounded-2xl border px-3 py-2 text-sm outline-none focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    />
  );
}

function Badge({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${className}`}>
      {children}
    </span>
  );
}

function formatMoney(amount, currency) {
  return formatCurrency(amount, currency);
}

function buildTrend(totalRevenue, occupancyRate) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"];
  const revenueFactors = [0.68, 0.74, 0.72, 0.81, 0.88, 0.94, 1];
  const occupancyFactors = [-5, -4, -3, -2, -1, 0, 0];

  return months.map((month, index) => ({
    month,
    revenue: Math.round(totalRevenue * revenueFactors[index]),
    occupancy: Math.max(0, Math.min(100, Math.round(occupancyRate + occupancyFactors[index]))),
  }));
}

function extractOrganizationId(profile, user, sessionClaims) {
  return (
    profile?.organization_id ||
    user?.app_metadata?.organization_id ||
    user?.app_metadata?.organizationId ||
    user?.user_metadata?.organization_id ||
    user?.user_metadata?.organizationId ||
    sessionClaims?.app_metadata?.organization_id ||
    sessionClaims?.app_metadata?.organizationId ||
    sessionClaims?.user_metadata?.organization_id ||
    sessionClaims?.user_metadata?.organizationId ||
    ""
  );
}

function getDaysPastDue(dueDate) {
  if (!dueDate) return 0;

  const due = new Date(`${dueDate}T00:00:00`);
  if (Number.isNaN(due.getTime())) return 0;

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const diff = startOfToday.getTime() - due.getTime();
  return Math.max(0, Math.floor(diff / 86400000));
}

function agingBucketFor(daysPastDue) {
  if (daysPastDue <= 0) return "Current";
  if (daysPastDue <= 30) return "1-30";
  if (daysPastDue <= 60) return "31-60";
  if (daysPastDue <= 90) return "61-90";
  return "90+";
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function getLogoUrl() {
  if (/^https?:\/\//i.test(logo)) return logo;
  return `${window.location.origin}${logo.startsWith("/") ? "" : "/"}${logo}`;
}

function getReportHtml(report, currency) {
  const generatedDate = new Date().toISOString().slice(0, 10);
  const logoUrl = getLogoUrl();
  const agingRows = report.aging
    .map(
      (item) => `
      <tr>
        <td>${escapeHtml(item.bucket)}</td>
        <td class="right">${escapeHtml(item.count)}</td>
        <td class="right">${escapeHtml(formatMoney(item.balance, currency))}</td>
      </tr>`
    )
    .join("");
  const overdueRows = report.topOverdue.length
    ? report.topOverdue
        .map(
          (invoice) => `
      <tr>
        <td><strong>${escapeHtml(invoice.tenant)}</strong><br><span>${escapeHtml(invoice.id)}</span></td>
        <td>${escapeHtml(invoice.property)}</td>
        <td>${escapeHtml(invoice.dueDate || "Not set")}</td>
        <td class="right">${escapeHtml(invoice.daysPastDue)}</td>
        <td class="right">${escapeHtml(formatMoney(invoice.balance, currency))}</td>
      </tr>`
        )
        .join("")
    : `<tr><td colspan="5" class="empty">No overdue invoice balances.</td></tr>`;
  const propertyRows = report.propertyPerformance.length
    ? report.propertyPerformance
        .map(
          (property) => `
      <tr>
        <td><strong>${escapeHtml(property.property)}</strong></td>
        <td class="right">${escapeHtml(formatMoney(property.revenue, currency))}</td>
        <td class="right">${escapeHtml(formatMoney(property.expenses, currency))}</td>
        <td class="right">${escapeHtml(formatMoney(property.netIncome, currency))}</td>
        <td class="right">${escapeHtml(`${property.occupancy}%`)}</td>
        <td class="right">${escapeHtml(property.maintenance)}</td>
      </tr>`
        )
        .join("")
    : `<tr><td colspan="6" class="empty">No property performance data.</td></tr>`;

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>SpaceM8 Portfolio Report</title>
  <style>
    body { margin: 0; background: #f8fafc; color: #0f172a; font-family: Arial, sans-serif; }
    .report { max-width: 940px; margin: 28px auto; background: #fff; padding: 42px; border: 1px solid #e2e8f0; }
    .top { display: flex; justify-content: space-between; gap: 28px; border-bottom: 2px solid #0f172a; padding-bottom: 22px; }
    .brand { display: flex; align-items: center; gap: 14px; }
    .brand img { width: 76px; height: auto; object-fit: contain; }
    h1 { margin: 0; font-size: 28px; letter-spacing: 0.04em; }
    h2 { margin: 28px 0 10px; font-size: 15px; text-transform: uppercase; letter-spacing: 0.12em; color: #64748b; }
    p { margin: 4px 0; line-height: 1.45; }
    .muted, span { color: #64748b; }
    .right { text-align: right; }
    .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-top: 24px; }
    .box { border: 1px solid #e2e8f0; background: #f8fafc; padding: 16px; }
    .box strong { display: block; margin-top: 8px; font-size: 18px; }
    table { width: 100%; border-collapse: collapse; margin-top: 10px; }
    th { background: #f1f5f9; color: #475569; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; padding: 11px; }
    td { border-bottom: 1px solid #e2e8f0; padding: 12px 11px; vertical-align: top; font-size: 13px; }
    .empty { color: #64748b; text-align: center; }
    .footer { margin-top: 34px; border-top: 1px solid #e2e8f0; padding-top: 16px; color: #64748b; font-size: 12px; }
    @media print { body { background: #fff; } .report { margin: 0; max-width: none; border: 0; } }
  </style>
</head>
<body>
  <main class="report">
    <section class="top">
      <div class="brand">
        <img src="${escapeHtml(logoUrl)}" alt="SpaceM8" />
        <div>
          <h1>PORTFOLIO REPORT</h1>
          <p class="muted">SpaceM8 Property Management</p>
        </div>
      </div>
      <div class="right">
        <p><strong>Executive Analytics</strong></p>
        <p>Generated: ${escapeHtml(generatedDate)}</p>
      </div>
    </section>

    <section class="summary">
      <div class="box"><span>Net Operating Income</span><strong>${escapeHtml(formatMoney(report.netOperatingIncome, currency))}</strong></div>
      <div class="box"><span>Total Expenses</span><strong>${escapeHtml(formatMoney(report.totalExpenses, currency))}</strong></div>
      <div class="box"><span>Outstanding</span><strong>${escapeHtml(formatMoney(report.outstandingBalance, currency))}</strong></div>
      <div class="box"><span>Occupancy</span><strong>${escapeHtml(`${report.occupancyRate}%`)}</strong></div>
    </section>

    <h2>Key Metrics</h2>
    <table>
      <tbody>
        <tr><td>Portfolio revenue</td><td class="right">${escapeHtml(formatMoney(report.portfolioRevenue, currency))}</td></tr>
        <tr><td>Expense ratio</td><td class="right">${escapeHtml(`${report.expenseRatio}%`)}</td></tr>
        <tr><td>Collections efficiency</td><td class="right">${escapeHtml(`${report.collectionsEfficiency}%`)}</td></tr>
        <tr><td>Maintenance closure</td><td class="right">${escapeHtml(`${report.maintenanceClosure}%`)}</td></tr>
        <tr><td>Overdue balance</td><td class="right">${escapeHtml(formatMoney(report.overdueBalance, currency))}</td></tr>
      </tbody>
    </table>

    <h2>Arrears Aging</h2>
    <table>
      <thead><tr><th>Bucket</th><th class="right">Invoices</th><th class="right">Balance</th></tr></thead>
      <tbody>${agingRows}</tbody>
    </table>

    <h2>Top Overdue Invoices</h2>
    <table>
      <thead><tr><th>Tenant</th><th>Property</th><th>Due</th><th class="right">Days</th><th class="right">Balance</th></tr></thead>
      <tbody>${overdueRows}</tbody>
    </table>

    <h2>Property Performance</h2>
    <table>
      <thead><tr><th>Property</th><th class="right">Revenue</th><th class="right">Expenses</th><th class="right">Net</th><th class="right">Occupancy</th><th class="right">Open Maintenance</th></tr></thead>
      <tbody>${propertyRows}</tbody>
    </table>

    <p class="footer">Generated from SpaceM8 live portfolio data.</p>
  </main>
</body>
</html>`;
}

function downloadHtml(filename, html) {
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function printReportDocument(report, currency, onBlocked = () => {}) {
  const printWindow = window.open("", "_blank", "width=950,height=720");
  if (!printWindow) {
    onBlocked();
    return;
  }

  printWindow.document.write(getReportHtml(report, currency));
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
}

export default function SpaceM8ReportsPage({
  isDark,
  setIsDark,
  currency,
  setCurrency,
}) {
  const toast = useToast();
  const { profile, user, sessionClaims } = useAuth();
  const [query, setQuery] = useState("");
  const [properties, setProperties] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentOrganizationId = extractOrganizationId(profile, user, sessionClaims);

  const fetchReportData = useCallback(async () => {
    setLoading(true);

    if (!currentOrganizationId) {
      setProperties([]);
      setInvoices([]);
      setPayments([]);
      setTickets([]);
      setExpenses([]);
      setLoading(false);
      return;
    }

    let propertiesQuery = supabase.from("properties").select("*").order("created_at", { ascending: false });
    let invoicesQuery = supabase.from("invoices").select("*").order("created_at", { ascending: false });
    let paymentsQuery = supabase.from("payments").select("invoice_id, amount, status");
    let ticketsQuery = supabase.from("maintenance_tickets").select("*").order("created_at", { ascending: false });
    let expensesQuery = supabase.from("expenses").select("*").order("expense_date", { ascending: false });

    if (currentOrganizationId) {
      propertiesQuery = propertiesQuery.eq("organization_id", currentOrganizationId);
      invoicesQuery = invoicesQuery.eq("organization_id", currentOrganizationId);
      paymentsQuery = paymentsQuery.eq("organization_id", currentOrganizationId);
      ticketsQuery = ticketsQuery.eq("organization_id", currentOrganizationId);
      expensesQuery = expensesQuery.eq("organization_id", currentOrganizationId);
    }

    const [
      { data: propertiesData, error: propertiesError },
      { data: invoicesData, error: invoicesError },
      { data: paymentsData, error: paymentsError },
      { data: ticketsData, error: ticketsError },
      { data: expensesData, error: expensesError },
    ] = await Promise.all([
      propertiesQuery,
      invoicesQuery,
      paymentsQuery,
      ticketsQuery,
      expensesQuery,
    ]);

    if (propertiesError) console.error("Error fetching report properties:", propertiesError);
    if (invoicesError) console.error("Error fetching report invoices:", invoicesError);
    if (paymentsError) console.error("Error fetching report payments:", paymentsError);
    if (ticketsError) console.error("Error fetching report maintenance tickets:", ticketsError);
    if (expensesError) console.error("Error fetching report expenses:", expensesError);

    setProperties(propertiesData || []);
    setInvoices(invoicesData || []);
    setPayments(paymentsData || []);
    setTickets(ticketsData || []);
    setExpenses(expensesData || []);
    setLoading(false);
  }, [currentOrganizationId]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const report = useMemo(() => {
    const totalUnits = properties.reduce((sum, item) => sum + Number(item.units || 0), 0);
    const occupiedUnits = properties.reduce((sum, item) => sum + Number(item.occupied || 0), 0);
    const portfolioRevenue = properties.reduce(
      (sum, item) => sum + Number(item.rent_per_unit || 0) * Number(item.occupied || 0),
      0
    );
    const occupancyRate = totalUnits > 0 ? Math.round((occupiedUnits / totalUnits) * 100) : 0;
    const totalExpenses = expenses.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const paidExpenses = expenses
      .filter((item) => item.status === "Paid")
      .reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const netOperatingIncome = portfolioRevenue - totalExpenses;
    const expenseRatio =
      portfolioRevenue > 0 ? Math.round((totalExpenses / portfolioRevenue) * 100) : 0;

    const invoiceTotal = invoices.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const paidByInvoice = payments.reduce((totalsByInvoice, payment) => {
      if (payment.invoice_id && payment.status === "Paid") {
        totalsByInvoice[payment.invoice_id] =
          (totalsByInvoice[payment.invoice_id] || 0) + Number(payment.amount || 0);
      }
      return totalsByInvoice;
    }, {});
    const paidTotal = Object.values(paidByInvoice).reduce((sum, amount) => sum + Number(amount || 0), 0);
    const collectionsEfficiency =
      invoiceTotal > 0 ? Math.round((paidTotal / invoiceTotal) * 100) : 0;
    const receivables = invoices
      .map((invoice) => {
        const amount = Number(invoice.amount || 0);
        const paidAmount =
          invoice.status === "Paid" && !paidByInvoice[invoice.id]
            ? amount
            : Number(paidByInvoice[invoice.id] || 0);
        const balance = Math.max(amount - paidAmount, 0);
        const daysPastDue = getDaysPastDue(invoice.due_date);

        return {
          id: invoice.id,
          tenant: invoice.tenant || "Unknown tenant",
          property: invoice.property || "Unassigned property",
          dueDate: invoice.due_date || "",
          amount,
          paidAmount,
          balance,
          daysPastDue,
          bucket: agingBucketFor(daysPastDue),
          status: invoice.status,
        };
      })
      .filter((invoice) => invoice.balance > 0 && invoice.status !== "Paid");
    const agingOrder = ["Current", "1-30", "31-60", "61-90", "90+"];
    const aging = agingOrder.map((bucket) => {
      const bucketInvoices = receivables.filter((invoice) => invoice.bucket === bucket);
      return {
        bucket,
        count: bucketInvoices.length,
        balance: bucketInvoices.reduce((sum, invoice) => sum + invoice.balance, 0),
      };
    });
    const outstandingBalance = receivables.reduce((sum, invoice) => sum + invoice.balance, 0);
    const overdueBalance = receivables
      .filter((invoice) => invoice.daysPastDue > 0)
      .reduce((sum, invoice) => sum + invoice.balance, 0);
    const topOverdue = [...receivables]
      .filter((invoice) => invoice.daysPastDue > 0)
      .sort((a, b) => b.balance - a.balance)
      .slice(0, 5);

    const completedTickets = tickets.filter((item) => item.status === "Completed").length;
    const maintenanceClosure =
      tickets.length > 0 ? Math.round((completedTickets / tickets.length) * 100) : 0;

    const propertyPerformance = properties.map((property) => {
      const units = Number(property.units || 0);
      const occupied = Number(property.occupied || 0);
      const revenue = Number(property.rent_per_unit || 0) * occupied;
      const propertyExpenses = expenses
        .filter((expense) => expense.property_name === property.name)
        .reduce((sum, expense) => sum + Number(expense.amount || 0), 0);

      return {
        property: property.name || "Unnamed property",
        revenue,
        expenses: propertyExpenses,
        netIncome: revenue - propertyExpenses,
        occupancy: units > 0 ? Math.round((occupied / units) * 100) : 0,
        maintenance: Number(property.maintenance_open || 0),
      };
    });

    return {
      portfolioRevenue,
      totalExpenses,
      paidExpenses,
      netOperatingIncome,
      expenseRatio,
      occupancyRate,
      collectionsEfficiency,
      outstandingBalance,
      overdueBalance,
      aging,
      topOverdue,
      maintenanceClosure,
      propertyPerformance,
      revenueTrend: buildTrend(portfolioRevenue, occupancyRate),
    };
  }, [properties, invoices, payments, tickets, expenses]);

  const reportCards = [
    {
      title: "Net Operating Income",
      value: formatMoney(report.netOperatingIncome, currency),
      change: `${properties.length} properties`,
      tone: "bg-emerald-100 text-emerald-700",
      icon: Wallet,
    },
    {
      title: "Total Expenses",
      value: formatMoney(report.totalExpenses, currency),
      change: `${report.expenseRatio}% of revenue`,
      tone: "bg-orange-100 text-orange-700",
      icon: ReceiptText,
    },
    {
      title: "Outstanding Balance",
      value: formatMoney(report.outstandingBalance, currency),
      change: `${formatMoney(report.overdueBalance, currency)} overdue`,
      tone: "bg-orange-100 text-orange-700",
      icon: Clock3,
    },
    {
      title: "Average Occupancy",
      value: `${report.occupancyRate}%`,
      change: "Live units",
      tone: "bg-sky-100 text-sky-700",
      icon: Building2,
    },
  ];

  const filteredPerformance = useMemo(() => {
    return report.propertyPerformance.filter((item) =>
      item.property.toLowerCase().includes(query.toLowerCase())
    );
  }, [report.propertyPerformance, query]);

  const handleExport = () => {
    downloadHtml("spacem8-portfolio-report.html", getReportHtml(report, currency));
  };

  const handlePrint = () => {
    printReportDocument(
      report,
      currency,
      () =>
        toast.error(
          "Report could not open",
          "Please allow pop-ups to print or save this report."
        )
    );
  };

  return (
    <AppLayout
      isDark={isDark}
      setIsDark={setIsDark}
      currency={currency}
      setCurrency={setCurrency}
      eyebrow="Executive Analytics"
      title="Reports"
      description="View performance trends, occupancy movement, revenue signals, and portfolio insights in one executive analytics workspace."
      actions={
        <>
          <Button
            variant="outline"
            className={`h-12 rounded-2xl px-4 ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
            onClick={fetchReportData}
          >
            <CalendarDays className="mr-2 h-4 w-4" />
            Refresh
          </Button>

          <Button onClick={handleExport} className="h-12 rounded-2xl px-5">
            <Download className="mr-2 h-4 w-4" />
            Export Report
          </Button>

          <Button onClick={handlePrint} className="h-12 rounded-2xl px-5">
            <Printer className="mr-2 h-4 w-4" />
            Print Report
          </Button>
        </>
      }
    >
      {loading ? (
        <div className={isDark ? "text-slate-300" : "text-slate-600"}>
          Loading reports...
        </div>
      ) : null}

      <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-4">
        {reportCards.map((item, index) => {
          const Icon = item.icon;

          return (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className={`rounded-[2rem] border p-5 shadow-md transition-shadow hover:shadow-lg ${
                isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {item.title}
                  </p>
                  <h3 className={`mt-3 text-3xl font-semibold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                    {item.value}
                  </h3>
                  <div className={`mt-3 inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm ${
                    isDark ? "bg-slate-800 text-slate-300" : "bg-slate-50 text-slate-600"
                  }`}>
                    <ArrowUpRight className="h-4 w-4 text-sky-600" />
                    {item.change}
                  </div>
                </div>
                <div className={`rounded-2xl p-3 ${item.tone}`}>
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </motion.div>
          );
        })}
      </section>

      <section className={`rounded-[2rem] border p-5 shadow-md ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
        <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className={`text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              Arrears aging
            </h2>
            <p className={`${isDark ? "text-slate-300" : "text-slate-600"} text-sm`}>
              Unpaid invoice balances grouped by how long they have been due
            </p>
          </div>
          <Badge className={report.overdueBalance > 0 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700"}>
            {formatMoney(report.overdueBalance, currency)} overdue
          </Badge>
        </div>

        <div className="grid gap-3 md:grid-cols-5">
          {report.aging.map((item) => (
            <div
              key={item.bucket}
              className={`rounded-3xl border p-4 ${isDark ? "border-slate-800 bg-slate-950/70" : "border-slate-200 bg-slate-50"}`}
            >
              <p className={`text-xs uppercase tracking-[0.18em] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {item.bucket}
              </p>
              <p className={`mt-3 text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                {formatMoney(item.balance, currency)}
              </p>
              <p className={`mt-2 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {item.count} invoice{item.count === 1 ? "" : "s"}
              </p>
            </div>
          ))}
        </div>

        <div className={`mt-5 overflow-hidden rounded-3xl border ${isDark ? "border-slate-800" : "border-slate-200"}`}>
          <div className={`grid grid-cols-5 gap-3 px-5 py-4 text-sm ${isDark ? "bg-slate-950 text-slate-300" : "bg-slate-50 text-slate-600"}`}>
            <span>Tenant</span>
            <span>Property</span>
            <span>Due Date</span>
            <span>Days</span>
            <span>Balance</span>
          </div>

          {report.topOverdue.length === 0 ? (
            <div className={`border-t px-5 py-6 text-center text-sm ${isDark ? "border-slate-800 text-slate-300" : "border-slate-200 text-slate-600"}`}>
              No overdue invoice balances.
            </div>
          ) : (
            report.topOverdue.map((invoice) => (
              <div
                key={invoice.id}
                className={`grid grid-cols-5 items-center gap-3 border-t px-5 py-4 text-sm ${isDark ? "border-slate-800" : "border-slate-200"}`}
              >
                <span className={`font-medium ${isDark ? "text-white" : "text-slate-900"}`}>
                  {invoice.tenant}
                </span>
                <span className={isDark ? "text-slate-300" : "text-slate-700"}>{invoice.property}</span>
                <span className={isDark ? "text-slate-300" : "text-slate-700"}>{invoice.dueDate || "Not set"}</span>
                <span className="font-medium text-amber-600">{invoice.daysPastDue}</span>
                <span className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                  {formatMoney(invoice.balance, currency)}
                </span>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="grid gap-6 2xl:grid-cols-[1.35fr_0.65fr]">
        <div className={`rounded-[2rem] border p-5 shadow-md ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h2 className={`text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                Revenue trend
              </h2>
              <p className={`${isDark ? "text-slate-300" : "text-slate-600"} text-sm`}>
                Portfolio revenue movement generated from live totals
              </p>
            </div>
            <Badge className="bg-sky-100 text-sky-700">Live data</Badge>
          </div>

          <div className={`rounded-3xl border p-4 ${isDark ? "border-slate-800 bg-slate-950/70" : "border-white/70 bg-white/80"}`}>
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={report.revenueTrend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="reportRevenueStroke" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#0ea5e9" />
                      <stop offset="100%" stopColor="#f97316" />
                    </linearGradient>
                    <linearGradient id="reportRevenueFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgba(14,165,233,0.25)" />
                      <stop offset="100%" stopColor="rgba(249,115,22,0.04)" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="4 6" stroke="#334155" vertical={false} />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fill: isDark ? "#94a3b8" : "#64748b", fontSize: 12 }} />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: isDark ? "#94a3b8" : "#64748b", fontSize: 12 }}
                    tickFormatter={(value) => `${Math.round(value / 1000)}k`}
                  />
                  <Tooltip
                    cursor={{ stroke: "#cbd5e1", strokeDasharray: "4 4" }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload || !payload.length) return null;
                      return (
                        <div className="min-w-[190px] rounded-2xl border border-slate-800 bg-slate-950/95 px-4 py-3 text-white shadow-2xl">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                            {label}
                          </p>
                          <p className="mt-3 text-2xl font-semibold tracking-tight">
                            {formatMoney(Number(payload[0].value || 0), currency)}
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="url(#reportRevenueStroke)"
                    fill="url(#reportRevenueFill)"
                    strokeWidth={3.5}
                    dot={{ r: 3, fill: "#f97316", stroke: "#ffffff", strokeWidth: 2 }}
                    activeDot={{ r: 7, fill: "#0ea5e9", stroke: "#ffffff", strokeWidth: 3 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className={`rounded-[2rem] border p-5 shadow-md ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <div className="mb-5">
            <h2 className={`text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              AI insight
            </h2>
            <p className={`${isDark ? "text-slate-300" : "text-slate-600"} text-sm`}>
              Executive summary of current performance
            </p>
          </div>

          <div className={`rounded-3xl border p-4 ${isDark ? "border-slate-800 bg-slate-950/60" : "border-sky-100 bg-sky-50"}`}>
            <div className="mb-3 flex items-center gap-2 text-sky-700">
              <BrainCircuit className="h-5 w-5" />
              <span className="font-medium">AI recommendation</span>
            </div>
            <p className={`text-sm leading-6 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Portfolio revenue is {formatMoney(report.portfolioRevenue, currency)} with {formatMoney(report.totalExpenses, currency)} in expenses, leaving net operating income of {formatMoney(report.netOperatingIncome, currency)}. Keep collections follow-up tight and review high-spend properties for margin improvement.
            </p>
          </div>
        </div>
      </section>

      <section className="grid gap-6 2xl:grid-cols-[0.8fr_1.2fr]">
        <div className={`rounded-[2rem] border p-5 shadow-md ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <div className="mb-5">
            <h2 className={`text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              Occupancy trend
            </h2>
            <p className={`${isDark ? "text-slate-300" : "text-slate-600"} text-sm`}>
              Portfolio occupancy generated from current units
            </p>
          </div>

          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={report.revenueTrend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 6" stroke="#334155" vertical={false} />
                <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fill: isDark ? "#94a3b8" : "#64748b", fontSize: 12 }} />
                <YAxis tickLine={false} axisLine={false} tick={{ fill: isDark ? "#94a3b8" : "#64748b", fontSize: 12 }} />
                <Tooltip
                  cursor={{ fill: "rgba(148,163,184,0.08)" }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload || !payload.length) return null;
                    return (
                      <div className="rounded-2xl border border-slate-800 bg-slate-950/95 px-4 py-3 text-white shadow-2xl">
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-400">{label}</p>
                        <p className="mt-2 text-xl font-semibold">{payload[0].value}%</p>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="occupancy" radius={[10, 10, 0, 0]} fill="#38bdf8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={`rounded-[2rem] border p-5 shadow-md ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className={`text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                Property performance
              </h2>
              <p className={`${isDark ? "text-slate-300" : "text-slate-600"} text-sm`}>
                Compare revenue and occupancy across your assets
              </p>
            </div>

            <div className="relative w-full max-w-sm">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search property..."
                className={`h-12 pl-11 pr-4 ${
                  isDark
                    ? "border-slate-700 bg-slate-900 text-white placeholder:text-slate-400"
                    : "border-slate-200 bg-white text-slate-800 placeholder:text-slate-500"
                }`}
              />
            </div>
          </div>

          <div className={`overflow-hidden rounded-3xl border ${isDark ? "border-slate-800" : "border-slate-200"}`}>
            <div className={`grid grid-cols-6 gap-3 px-5 py-4 text-sm ${isDark ? "bg-slate-900 text-slate-300" : "bg-gradient-to-br from-white to-sky-50 text-slate-600"}`}>
              <span>Property</span>
              <span>Revenue</span>
              <span>Expenses</span>
              <span>Net</span>
              <span>Occupancy</span>
              <span>Maintenance</span>
            </div>

            {filteredPerformance.length === 0 ? (
              <div className={`border-t px-5 py-6 text-center text-sm ${isDark ? "border-slate-800 text-slate-300" : "border-slate-200 text-slate-600"}`}>
                No properties found.
              </div>
            ) : (
              filteredPerformance.map((item) => (
                <div
                  key={item.property}
                  className={`grid grid-cols-6 items-center gap-3 border-t px-5 py-4 text-sm ${
                    isDark ? "border-slate-800" : "border-slate-200"
                  }`}
                >
                  <span className={`font-medium ${isDark ? "text-white" : "text-slate-900"}`}>
                    {item.property}
                  </span>
                  <span className={isDark ? "text-slate-300" : "text-slate-700"}>
                    {formatMoney(item.revenue, currency)}
                  </span>
                  <span className={isDark ? "text-slate-300" : "text-slate-700"}>
                    {formatMoney(item.expenses, currency)}
                  </span>
                  <span className={`font-medium ${item.netIncome >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {formatMoney(item.netIncome, currency)}
                  </span>
                  <span className={isDark ? "text-slate-300" : "text-slate-700"}>
                    {item.occupancy}%
                  </span>
                  <span className={isDark ? "text-slate-300" : "text-slate-700"}>
                    {item.maintenance}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </AppLayout>
  );
}
